import axios, { AxiosHeaderValue, AxiosInstance, AxiosResponse } from 'axios';
import * as process from 'process';

import { CookieManager } from '../utils/cookieManager';
import { API_URLS } from 'utils/apiUrlBuilder';

// Types and Interfaces
interface ApiConfig {
  baseURL: string | undefined;
  headers?: Record<string, string>;
}

interface ApiMethods {
  get<T = any>(url: string, config?: any): Promise<AxiosResponse<T>>;
  post<T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>>;
  put<T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>>;
  patch<T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>>;
  delete<T = any>(url: string, config?: any): Promise<AxiosResponse<T>>;
}

// Environment Variables Validation
const validateEnvVariables = () => {
  const requiredEnvVars = [
    'REACT_APP_BASE_URL',
    'REACT_APP_SERVICES_BASE_URL',
    'REACT_APP_CAND_URL',
    'REACT_APP_STRAPI_BASE_URL',
    'REACT_APP_URL_AWS_COGNITO_USER_POOL',
  ];

  const missingVars = requiredEnvVars.filter(envVar => !process.env[envVar]);

  if (missingVars.length > 0) {
    console.error('Missing required environment variables:', missingVars.join(', '));
  }
};

// API Configuration
const API_CONFIG = {
  // Core APIs
  main: process.env.REACT_APP_BASE_URL,
  user: API_URLS.CAND_CORE_API,
  rainmakersV2: process.env.REACT_APP_BASE_URL,

  // Platform APIs
  advisor: API_URLS.ADVISOR,
  meeting: API_URLS.MEETING,
  orchestrator: API_URLS.ORCHESTRATOR_POLICY_V2,

  // External Services
  copilot: API_URLS.COPILOT,
  networking: API_URLS.NETWORKING_API,
  pbi: API_URLS.PBI,
  vox: API_URLS.VOX,
  forms: API_URLS.FORMS,
  notifications: API_URLS.NOTIFICATIONS_API,

  // CMS APIs
  strapi: API_URLS.STRAPI_API,
} as const;

/* const API_HEADERS = {
  user: {
    'X-Cognito-UserPool': process.env.REACT_APP_URL_AWS_COGNITO_USER_POOL,
  },
} as const; */

// Core Service Classes
class ApiService {
  private static instances: Map<string, AxiosInstance> = new Map();
  private static interceptorIds: Map<string, { request: number; response: number }> = new Map();

  private static createInstance(config: ApiConfig, key?: string): AxiosInstance {
    const baseURL = config.baseURL || '';

    if (!baseURL) {
      console.warn('Warning: Base URL is not defined. Using empty string as fallback.');
    }

    const instance = axios.create({
      baseURL,
      // Para orchestrator, sempre usar withCredentials para enviar cookies
      withCredentials: key === 'orchestrator',
      headers: {
        ...config.headers,
        //...(key && API_HEADERS[key as keyof typeof API_HEADERS]),
      },
    });

    // Log da configuração da instância
    if (key === 'orchestrator') {
      console.log('[ApiService] 🏗️ Instância orchestrator criada:', {
        baseURL,
        withCredentials: true,
        timestamp: new Date().toISOString(),
      });
    }

    // Request interceptor to ensure headers are present in every request
    const requestInterceptor = instance.interceptors.request.use(
      config => {
        // Ensure headers object exists
        config.headers = config.headers || {};

        // Para orchestrator, withCredentials já está configurado globalmente
        if (key === 'orchestrator') {
          console.log(
            '[ApiService] 🔑 Requisição orchestrator - withCredentials já configurado globalmente:',
            config.url
          );

          // Para refresh-token, remover headers que disparam preflight
          if (config.url?.includes('/auth/refresh-token')) {
            // Remover headers que podem causar preflight CORS
            if (config.headers?.Authorization) {
              delete config.headers.Authorization;
            }
            if (config.headers?.['Content-Type']) {
              delete config.headers['Content-Type'];
            }
            console.log('[ApiService] 🔄 Headers removidos para refresh-token (evitar preflight)');
          } else {
            console.log('[ApiService] 🔑 Endpoint orchestrator normal para:', config.url);
          }
        }

        return config;
      },
      error => {
        return Promise.reject(error);
      }
    );

    // Response interceptor with refresh token support
    const responseInterceptor = instance.interceptors.response.use(
      response => {
        // Log detalhado para debug de cookies
        if (key === 'orchestrator' && response.config.url?.includes('/auth/login')) {
          console.log('[ApiService] 🔍 Debug de cookies no interceptor de resposta:', {
            url: response.config.url,
            status: response.status,
            headers: response.headers,
            'set-cookie-header': response.headers['set-cookie'],
            'Set-Cookie-header': response.headers['Set-Cookie'],
            'all-headers': Object.keys(response.headers),
          });
        }

        // Add cache control headers to prevent caching issues
        response.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
        response.headers['Pragma'] = 'no-cache';
        response.headers['Expires'] = '0';

        // Se o backend renovou o token de forma proativa, atualizar Authorization
        const newTokenHeader = (response.headers as any)?.['x-new-access-token'] as
          | string
          | undefined;
        if (newTokenHeader) {
          try {
            // Atualiza headers padrão de todas as instâncias
            const orgUUID = localStorage.getItem('x-organization-uuid') || undefined;
            const cognitoUserPool = localStorage.getItem('x-cognito-userpool') || undefined;
            defaultsHeadersAxios(newTokenHeader, orgUUID, cognitoUserPool);
            // Espelha token no storage/cookie (debug/consistência)
            localStorage.setItem('AUTH_TOKEN', newTokenHeader);
            CookieManager.setAccessToken(newTokenHeader);
            // Log leve para diagnóstico
            console.log('[ApiService] X-New-Access-Token detectado. Authorization atualizado.');
          } catch (e) {
            console.warn('[ApiService] Falha ao aplicar X-New-Access-Token:', e);
          }
        }

        return response;
      },
      async error => {
        const originalRequest = error.config;

        // Se for erro 401 e não for uma tentativa de refresh
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          console.log(
            '[ApiService] 🔄 Interceptor: Erro 401 detectado, iniciando refresh automático...',
            {
              url: originalRequest.url,
              method: originalRequest.method,
              hasRetry: originalRequest._retry,
              timestamp: new Date().toISOString(),
            }
          );

          try {
            // Tentar refresh do token
            const { RefreshTokenService } = await import('./refreshTokenService');
            console.log('[ApiService] 📦 RefreshTokenService importado com sucesso');

            const newToken = await RefreshTokenService.refreshAccessToken();

            if (newToken) {
              console.log('[ApiService] ✅ Token renovado com sucesso!', {
                newTokenLength: newToken.length,
                timestamp: new Date().toISOString(),
              });

              // Atualizar header de autorização
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              console.log('[ApiService] 🔑 Header Authorization atualizado, fazendo retry...');

              // Retry da requisição original
              return instance(originalRequest);
            } else {
              console.warn('[ApiService] ⚠️ Refresh retornou token nulo');
            }
          } catch (refreshError) {
            console.error('[ApiService] ❌ Falha crítica ao renovar token:', {
              error: refreshError,
              message: refreshError instanceof Error ? refreshError.message : 'Erro desconhecido',
              stack: refreshError instanceof Error ? refreshError.stack : undefined,
              timestamp: new Date().toISOString(),
            });

            // A limpeza completa já foi feita pelo RefreshTokenService
            // Apenas logamos que o interceptor detectou a falha
            console.log('[ApiService] ℹ️ Limpeza completa já executada pelo RefreshTokenService');
          }
        } else if (error.response?.status === 401) {
          console.log('[ApiService] ℹ️ Erro 401 ignorado (já tentou refresh ou não é retry)', {
            url: originalRequest.url,
            hasRetry: originalRequest._retry,
            timestamp: new Date().toISOString(),
          });
        }

        // Log do erro original
        console.error('API Error:', {
          url: error.config?.url,
          status: error.response?.status,
          data: error.response?.data,
        });

        return Promise.reject(error);
      }
    );

    // Store interceptor IDs for potential cleanup
    this.interceptorIds.set(key || 'default', {
      request: requestInterceptor,
      response: responseInterceptor,
    });

    return instance;
  }

  public static getInstance(key: string, config: ApiConfig): AxiosInstance {
    if (!this.instances.has(key)) {
      this.instances.set(key, this.createInstance(config, key));
    }
    return this.instances.get(key)!;
  }

  // Method to clear interceptors if needed
  public static clearInterceptors(key: string): void {
    const instance = this.instances.get(key);
    const ids = this.interceptorIds.get(key);

    if (instance && ids) {
      instance.interceptors.request.eject(ids.request);
      instance.interceptors.response.eject(ids.response);
      this.interceptorIds.delete(key);
    }
  }
}

class ApiWrapper implements ApiMethods {
  private key: string;
  private baseURL: string | undefined;

  constructor(key: string, baseURL: string | undefined) {
    this.key = key;
    this.baseURL = baseURL;
  }

  public getInstance(): AxiosInstance {
    return ApiService.getInstance(this.key, { baseURL: this.baseURL });
  }

  public get defaults() {
    const instance = this.getInstance();
    return {
      headers: {
        common: {
          get Authorization(): AxiosHeaderValue | undefined {
            return instance.defaults.headers.common.Authorization;
          },
          set Authorization(value: AxiosHeaderValue | undefined) {
            instance.defaults.headers.common.Authorization = value;
          },
        },
      },
    };
  }

  // HTTP Methods
  get<T = any>(url: string, config?: any): Promise<AxiosResponse<T>> {
    return this.getInstance().get(url, config);
  }

  post<T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> {
    return this.getInstance().post(url, data, config);
  }

  put<T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> {
    return this.getInstance().put(url, data, config);
  }

  patch<T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> {
    return this.getInstance().patch(url, data, config);
  }

  delete<T = any>(url: string, config?: any): Promise<AxiosResponse<T>> {
    return this.getInstance().delete(url, config);
  }

  // Helper Methods
  setAuthToken(token: string | undefined, type: 'Token' | 'Bearer' = 'Token'): void {
    const authValue = token ? `${type} ${token}` : undefined;
    this.getInstance().defaults.headers.common.Authorization = authValue;
  }

  setOrganizationUUID(uuid: string | undefined): void {
    this.getInstance().defaults.headers.common['X-Organization-UUID'] = uuid;
  }
}

// API Instances
export const apiMain = new ApiWrapper('main', API_CONFIG.main);
export const apiUser = new ApiWrapper('user', API_CONFIG.user);
export const apiRainmakersV2 = new ApiWrapper('rainmakersV2', API_CONFIG.rainmakersV2);

export const apiAdvisor = new ApiWrapper('advisor', API_CONFIG.advisor);
export const apiMeeting = new ApiWrapper('meeting', API_CONFIG.meeting);
export const apiOrchestrator = new ApiWrapper('orchestrator', API_CONFIG.orchestrator);

export const apiCopilot = new ApiWrapper('copilot', API_CONFIG.copilot);
export const apiNetworking = new ApiWrapper('networking', API_CONFIG.networking);
export const apiPBI = new ApiWrapper('pbi', API_CONFIG.pbi);
export const apiVox = new ApiWrapper('vox', API_CONFIG.vox);
export const apiForms = new ApiWrapper('forms', API_CONFIG.forms);
export const apiNotifications = new ApiWrapper('notifications', API_CONFIG.notifications);

// CMS APIs
export const apiStrapi = new ApiWrapper('strapi', API_CONFIG.strapi);

// Header Configuration Functions
export const defaultsHeadersAxios = (
  token: string,
  organizationUUID?: string,
  userPool?: string
): void => {
  const apis = [
    apiMain,
    apiUser,
    apiRainmakersV2,
    apiAdvisor,
    apiMeeting,
    apiOrchestrator,
    apiCopilot,
    apiNetworking,
    apiPBI,
    apiVox,
    apiForms,
    apiStrapi,
    apiNotifications,
  ].map(api => api.getInstance());

  apis.forEach(apiInstance => {
    // Remove undefined headers
    Object.keys(apiInstance.defaults.headers.common).forEach(key => {
      if (apiInstance.defaults.headers.common[key] === undefined) {
        delete apiInstance.defaults.headers.common[key];
      }
    });

    // Set new headers
    apiInstance.defaults.headers.common = {
      ...apiInstance.defaults.headers.common,
      Authorization: token ? `Bearer ${token}` : undefined,
    };

    // Add optional headers if provided
    if (organizationUUID) {
      apiInstance.defaults.headers.common['X-Organization-UUID'] = organizationUUID;
    }

    if (userPool) {
      apiInstance.defaults.headers.common['X-Cognito-UserPool'] = userPool;
    }
  });
};

// Separate function for Strapi headers
export const setupStrapiHeaders = (
  token: string | undefined,
  organizationUUID?: string,
  userPool?: string
): boolean => {
  const instance = apiStrapi.getInstance();

  // Get headers from localStorage or params
  const cognitoUserPool = userPool || localStorage.getItem('x-cognito-userpool');
  const orgUUID = organizationUUID || localStorage.getItem('x-organization-uuid');
  const authToken = token || localStorage.getItem('AUTH_TOKEN');

  // Required headers validation with detailed logging
  if (!authToken) {
    console.error('Strapi API: Token is required');
    return false;
  }

  if (!cognitoUserPool) {
    console.error('Strapi API: Cognito User Pool is not configured in environment');
    return false;
  }

  if (!orgUUID) {
    console.error('Strapi API: Organization UUID is required');
    return false;
  }

  try {
    // Reset headers
    instance.defaults.headers.common = {};

    // Set new headers
    const headers = {
      Authorization: `Bearer ${authToken}`,
      'X-Cognito-UserPool': cognitoUserPool,
      'X-Organization-UUID': orgUUID,
    };

    // Apply headers
    instance.defaults.headers.common = headers;

    // Verify headers were set correctly
    const currentHeaders = instance.defaults.headers.common;
    const missingHeaders = Object.entries(headers).filter(([key]) => !currentHeaders[key]);

    if (missingHeaders.length > 0) {
      console.error('Strapi API: Headers not set correctly:', {
        missing: missingHeaders,
        current: currentHeaders,
      });
      return false;
    }

    return true;
  } catch (error) {
    console.error('Strapi API: Error setting headers:', error);
    return false;
  }
};

// Initialize environment validation
validateEnvVariables();
